import React, { useState, useMemo } from "react";
import AssignmentOutlined from "@material-ui/icons/AssignmentOutlined";

import {
  Card,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
  CircularProgress,
  Box,
  Tooltip,
  Stack,
  IconButton,
} from "@material-ui/core";
import { removeAccents } from "../../utils/converter";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import Scrollbar from "../../components/Scrollbar";
import { TablaHead } from "../../components/_dashboard/tablas";
import * as locales from "@material-ui/core/locale";
import Page from "../../components/Page";
import SubTareasDialogCliente from "./SubTareasDialogCliente";
import { AlertaMensajeEliminacion } from "../AlertaMensajeEliminacion";
import Search from "@material-ui/icons/Search";
import {
  esFechaMayorOIgual,
  esFechaMenorOIgual,
  formatearFecha,
} from "src/utils/formatTime";
import { useObtenerSubtareasClienteQuery } from "src/services/accesoClientesApi";
import { useClienteActual } from "src/hooks/useClienteActual";
import { useSubtareasProgreso } from "./hooks/useSubtareasProgreso";

import { useClienteFilters } from "src/components/cliente/hooks/useClienteFilters";
import ClienteFilterPanel from "src/components/cliente/filters/ClienteFilterPanel";
import TableSkeleton from "src/components/cliente/loading/TableSkeleton";
import EmptyState from "src/components/cliente/empty/EmptyState";
import StatusBadge from "src/components/cliente/badges/StatusBadge";

const TABLE_HEAD = [
  { id: "nombre", label: "Tarea", alignRight: false },
  { id: "tipoTarea", label: "Tipo", alignRight: false },
  { id: "estado", label: "Estado", alignRight: false },
  { id: "fechaInicio", label: "Fecha Inicio", alignRight: false },
  { id: "fechaTermino", label: "Fecha Término", alignRight: false },
  { id: "progreso", label: "Progreso", alignRight: false },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export default function TareasTerminadas(props) {
  const { tareasCliente } = props;
  const { rut } = useClienteActual();

  // Estados locales
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("nombre");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [subTareaSeleccionada, setSubTareaSeleccionada] = useState(null);
  const [mostrarSubTareas, setMostrarSubTarea] = useState(false);
  const [mostrarAlerta, setMostrarAlerta] = useState(false);
  const [locale] = useState("esES");

  // Hook de filtros para tareas
  const {
    filtros,
    updateFiltro,
    toggleEstado,
    limpiarFiltros,
    removerFiltro,
    filtrosActivos,
    hayFiltrosActivos,
  } = useClienteFilters("tareas");

  // RTK Query - Obtener subtareas del cliente
  const { data: subTareasData = [], isLoading: isLoadingSubtareas } =
    useObtenerSubtareasClienteQuery(rut);

  // Calcular porcentajes de subtareas
  const { subtareas: subTareas, porcentajes: porcentajeSubTareas } =
    useSubtareasProgreso(subTareasData, tareasCliente);

  // Filtrar tareas SOLO terminadas
  const tareasTerminadas = useMemo(
    () => tareasCliente.filter((e) => e.estado === "TERMINADA"),
    [tareasCliente],
  );

  // Aplicar filtros a las tareas terminadas
  const tareasFiltradas = useMemo(() => {
    let resultado = [...tareasTerminadas];

    // Filtro por búsqueda (nombre o tipo)
    if (filtros.busqueda) {
      resultado = resultado.filter((tarea) => {
        const searchTerm = removeAccents(filtros.busqueda.toLowerCase());
        return (
          removeAccents(tarea.nombre.toLowerCase()).includes(searchTerm) ||
          removeAccents(tarea.tipoTarea.toLowerCase()).includes(searchTerm)
        );
      });
    }

    // Filtro por tipo de tarea
    if (filtros.tipoTarea) {
      resultado = resultado.filter(
        (tarea) => tarea.tipoTarea === filtros.tipoTarea,
      );
    }

    // Filtro por fecha inicio desde
    if (filtros.fechaInicioDesde) {
      resultado = resultado.filter((tarea) =>
        esFechaMayorOIgual(tarea.fechaInicio, filtros.fechaInicioDesde),
      );
    }

    // Filtro por fecha inicio hasta
    if (filtros.fechaInicioHasta) {
      resultado = resultado.filter((tarea) =>
        esFechaMenorOIgual(tarea.fechaInicio, filtros.fechaInicioHasta),
      );
    }

    return resultado;
  }, [tareasTerminadas, filtros]);

  // Aplicar ordenamiento a las tareas ya filtradas
  const tareasOrdenadas = useMemo(
    () => applySortFilter(tareasFiltradas, getComparator(order, orderBy)),
    [tareasFiltradas, order, orderBy],
  );

  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - tareasOrdenadas.length)
      : 0;

  const noHayResultados = tareasOrdenadas.length === 0;

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = tareasCliente.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Componente auxiliar para mostrar progreso
  function CircularProgressWithLabel(props) {
    return (
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <CircularProgress variant="determinate" {...props} />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="caption" component="div" color="text.secondary">
            {`${Math.round(props.value)}%`}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Page title="TAXTIC">
      <Container>
        {/* Panel de filtros */}
        <ClienteFilterPanel
          tipo="tareas"
          filtros={filtros}
          updateFiltro={updateFiltro}
          toggleEstado={toggleEstado}
          limpiarFiltros={limpiarFiltros}
          removerFiltro={removerFiltro}
          filtrosActivos={filtrosActivos}
          hayFiltrosActivos={hayFiltrosActivos}
          searchPlaceholder="Buscar tarea terminada..."
        />

        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              {/* Loading state */}
              {isLoadingSubtareas ? (
                <TableSkeleton
                  rows={5}
                  headers={[
                    "Tarea",
                    "Tipo",
                    "Estado",
                    "Fecha Inicio",
                    "Fecha Término",
                    "Progreso",
                  ]}
                  showActions={false}
                />
              ) : (
                <>
                  {/* Estado vacío */}
                  {noHayResultados ? (
                    <EmptyState
                      icon={AssignmentOutlined}
                      title={
                        hayFiltrosActivos
                          ? "No se encontraron tareas terminadas"
                          : "No tienes tareas terminadas"
                      }
                      description={
                        hayFiltrosActivos
                          ? "Intenta ajustar los filtros de búsqueda"
                          : "Aún no hay tareas completadas"
                      }
                    />
                  ) : (
                    <Table>
                      <TablaHead
                        order={order}
                        orderBy={orderBy}
                        headLabel={TABLE_HEAD}
                        rowCount={tareasOrdenadas.length}
                        numSelected={selected.length}
                        onRequestSort={handleRequestSort}
                        onSelectAllClick={handleSelectAllClick}
                      />
                      <TableBody>
                        {tareasOrdenadas
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          .map((row) => {
                            const {
                              id,
                              nombre,
                              tipoTarea,
                              fechaInicio,
                              fechaTermino,
                              estado,
                            } = row;
                            const isItemSelected =
                              selected.indexOf(nombre) !== -1;

                            // Calcular porcentaje de subtareas
                            let porcentajeSubtareasAux = 0;
                            if (
                              porcentajeSubTareas &&
                              porcentajeSubTareas.length >= 1
                            ) {
                              porcentajeSubtareasAux =
                                porcentajeSubTareas.filter(
                                  (e) => e.idTarea === id,
                                )[0];
                              if (porcentajeSubtareasAux?.porcentaje) {
                                porcentajeSubtareasAux =
                                  porcentajeSubtareasAux.porcentaje;
                              } else {
                                porcentajeSubtareasAux = 0;
                              }
                            }

                            return (
                              <TableRow
                                hover
                                key={id}
                                tabIndex={-1}
                                role="checkbox"
                                selected={isItemSelected}
                                aria-checked={isItemSelected}
                              >
                                {/* Nombre de la tarea */}
                                <TableCell align="left" component="th">
                                  <Typography variant="subtitle2">
                                    {nombre}
                                  </Typography>
                                </TableCell>

                                {/* Tipo de tarea con badge */}
                                <TableCell align="left">
                                  <StatusBadge
                                    estado={tipoTarea}
                                    tipo="tipoTarea"
                                  />
                                </TableCell>

                                {/* Estado con badge (siempre TERMINADA) */}
                                <TableCell align="left">
                                  <StatusBadge estado={estado} tipo="tarea" />
                                </TableCell>

                                {/* Fecha de inicio */}
                                <TableCell align="left">
                                  {fechaInicio === ""
                                    ? "-"
                                    : formatearFecha(
                                        fechaInicio,
                                        "DD/MM/YYYY",
                                        "YYYY/MM/DD",
                                      )}
                                </TableCell>

                                {/* Fecha de término */}
                                <TableCell align="left">
                                  {fechaTermino === ""
                                    ? "-"
                                    : formatearFecha(
                                        fechaTermino,
                                        "DD/MM/YYYY",
                                        "YYYY/MM/DD",
                                      )}
                                </TableCell>

                                {/* Progreso con subtareas */}
                                <TableCell
                                  align="left"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    let subTareaObtenida = subTareas.filter(
                                      (e) => e.idTarea === id,
                                    );
                                    if (subTareaObtenida.length >= 1) {
                                      setSubTareaSeleccionada(subTareaObtenida);
                                      setMostrarSubTarea(true);
                                    } else {
                                      setMostrarAlerta(true);
                                    }
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                  >
                                    <CircularProgressWithLabel
                                      value={porcentajeSubtareasAux}
                                    />
                                    <Tooltip title="Ver Progreso">
                                      <IconButton size="small">
                                        <Search />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        {emptyRows > 0 && (
                          <TableRow style={{ height: 53 * emptyRows }}>
                            <TableCell colSpan={6} />
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </>
              )}
            </TableContainer>
          </Scrollbar>

          <ThemeProvider
            theme={(outerTheme) => createTheme(outerTheme, locales[locale])}
          >
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={tareasOrdenadas.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </ThemeProvider>
        </Card>
      </Container>

      {mostrarSubTareas && subTareaSeleccionada && (
        <SubTareasDialogCliente
          open={mostrarSubTareas}
          setOpen={setMostrarSubTarea}
          subTareas={subTareaSeleccionada}
        />
      )}

      {mostrarAlerta && (
        <AlertaMensajeEliminacion
          mensaje="No existen tareas que mostrar."
          open={mostrarAlerta}
          setOpen={setMostrarAlerta}
          aceptar={() => {
            setMostrarAlerta(false);
          }}
          mensajeBotonAceptar="Cerrar"
          ocultar="Ocultar"
        />
      )}
    </Page>
  );
}
